import { useState } from "react";
import { toast } from "sonner";

import {
  type CarrierTrip,
  useArchiveCarrierTrip,
  useDeleteCarrierTrip,
  useUnassignTripOperator,
} from "@/api/carrier";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import { AssignVehicleDialog } from "@/features/assign-vehicle-dialog";
import { EditTripColorDialog } from "@/features/edit-trip-color-dialog";
import { tripLabel } from "./dispatch-presenters";

/**
 * Per-trip actions on a dispatch card.
 *
 * Everything here already existed on the Trips screen; a dispatcher just had to
 * leave the board to reach it, which meant losing the staged work in front of
 * them. The mutations and dialogs are the SAME ones — this is a second door to
 * one room, not a second room.
 *
 * Actions are gated by the Server's own rules rather than offered and allowed
 * to fail: archive is COMPLETED-only, delete is PLANNED-only.
 */
export function TripActionsMenu({ trip }: { trip: CarrierTrip }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [vehicleOpen, setVehicleOpen] = useState(false);

  const unassign = useUnassignTripOperator();
  const archive = useArchiveCarrierTrip();
  const deleteTrip = useDeleteCarrierTrip();

  const label = tripLabel(trip);

  return (
    <>
      <RowMenu label={`Actions for ${label}`} className="h-7 w-7">
        <RowMenuItem onClick={() => setColorOpen(true)}>Edit color</RowMenuItem>
        <RowMenuItem onClick={() => setVehicleOpen(true)}>
          Assign vehicle
        </RowMenuItem>
        {trip.operatorId && (
          <RowMenuItem
            disabled={unassign.isPending}
            onClick={() =>
              unassign.mutate(trip.id, {
                onSuccess: () => toast.success("Driver unassigned."),
                onError: () => toast.error("Failed to unassign driver."),
              })
            }
          >
            Unassign driver
          </RowMenuItem>
        )}
        {trip.status === "COMPLETED" && (
          <RowMenuItem
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate(trip.id, {
                onSuccess: () => toast.success("Trip archived."),
                onError: () => toast.error("Failed to archive trip."),
              })
            }
          >
            Archive trip
          </RowMenuItem>
        )}
        {trip.status === "PLANNED" && (
          <RowMenuItem
            variant="destructive"
            onSelect={(event) => {
              event.preventDefault();
              setConfirmDelete(true);
            }}
          >
            Delete trip
          </RowMenuItem>
        )}
      </RowMenu>

      <EditTripColorDialog
        open={colorOpen}
        onOpenChange={setColorOpen}
        tripId={trip.id}
        initialColor={trip.color}
      />
      <AssignVehicleDialog
        open={vehicleOpen}
        onOpenChange={setVehicleOpen}
        trip={trip}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
            <AlertDialogDescription>
              The trip's stops return to the unassigned queue and can be
              re-routed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTrip.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteTrip.isPending}
              onClick={() =>
                deleteTrip.mutate(trip.id, {
                  onSuccess: () => {
                    toast.success("Trip deleted.");
                    setConfirmDelete(false);
                  },
                  onError: () => toast.error("Failed to delete trip."),
                })
              }
            >
              Delete trip
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
